import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Alert,
  Tabs,
  Tab,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Map,
  Public,
  Person,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { format } from 'date-fns';

interface MarketAccess {
  id: number;
  user_id: string;
  market_id: number;
  market_name?: string;
  country_name?: string;
  access_level: 'view' | 'manage' | 'admin';
  granted_by: string;
  granted_by_name?: string;
  granted_at: string;
  expires_at?: string;
  is_active: boolean;
  notes?: string;
}

interface CountryAccess {
  id: number;
  user_id: string;
  country_id: number;
  country_name?: string;
  market_count?: number;
  access_level: 'view' | 'manage' | 'admin';
  granted_by: string;
  granted_by_name?: string;
  granted_at: string;
  expires_at?: string;
  is_active: boolean;
  notes?: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  totalMarkets?: number;
  totalCountries?: number;
}

interface Market {
  id: number;
  display_name: string;
  country_name: string;
}

interface Country {
  id: number;
  name: string;
  market_count?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`market-access-tabpanel-${index}`}
      aria-labelledby={`market-access-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const MarketAccessTab: React.FC = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<any>(null);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [grantAccessDialog, setGrantAccessDialog] = useState(false);
  const [viewUserAccessDialog, setViewUserAccessDialog] = useState(false);
  const [viewMarketUsersDialog, setViewMarketUsersDialog] = useState(false);
  const [viewCountryUsersDialog, setViewCountryUsersDialog] = useState(false);
  const [expandedMarkets, setExpandedMarkets] = useState<Set<number>>(new Set());
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [includeIndirectCountryAccess, setIncludeIndirectCountryAccess] = useState(false);
  const [includeIndirectMarketAccess, setIncludeIndirectMarketAccess] = useState(false);

  // New state for grant access form
  const [grantForm, setGrantForm] = useState({
    user_id: '',
    country_id: null as number | null,
    market_id: null as number | null | 'all',
    access_level: 'manage' as 'view' | 'manage' | 'admin',
    expires_at: '',
    notes: '',
  });

  // Fetch all users for grant dialog
  const { data: allUsersData } = useQuery({
    queryKey: ['all-users'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/users`);
      if (!response || !response.ok) throw new Error('Failed to fetch users');
      return await response.json();
    },
    enabled: grantAccessDialog,
  });

  // Fetch users with market access (for By User tab)
  const { data: usersWithAccessData, isLoading: usersLoading } = useQuery({
    queryKey: ['users-with-market-access'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/market-access/users`);
      if (!response || !response.ok) throw new Error('Failed to fetch users with market access');
      return await response.json();
    },
  });

  // Fetch markets with users (for By Market tab)
  const { data: marketsWithUsersData, isLoading: marketsLoading } = useQuery({
    queryKey: ['markets-with-users'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/market-access/markets`);
      if (!response || !response.ok) throw new Error('Failed to fetch markets with users');
      return await response.json();
    },
  });

  // Fetch countries with users (for By Country tab)
  const { data: countriesWithUsersData, isLoading: countriesLoading } = useQuery({
    queryKey: ['countries-with-users'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/market-access/countries`);
      if (!response || !response.ok) throw new Error('Failed to fetch countries with users');
      return await response.json();
    },
  });

  // Fetch all countries - only when grant dialog is open
  const { data: countriesData } = useQuery({
    queryKey: ['all-countries'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/store/markets/countries`);
      if (!response || !response.ok) throw new Error('Failed to fetch countries');
      return await response.json();
    },
    enabled: grantAccessDialog,
  });

  // Fetch markets for selected country - only when country is selected
  const { data: countryMarketsData } = useQuery({
    queryKey: ['country-markets', grantForm.country_id],
    queryFn: async () => {
      if (!grantForm.country_id) return null;
      const response = await authenticatedFetch(`${API_URL}/api/store/markets/${grantForm.country_id}`);
      if (!response || !response.ok) throw new Error('Failed to fetch markets for country');
      return await response.json();
    },
    enabled: !!grantForm.country_id && grantAccessDialog,
  });

  // Fetch user market access when a user is selected
  const { data: userAccessData, isLoading: accessLoading, refetch: refetchUserAccess } = useQuery({
    queryKey: ['user-market-access', selectedUser?.id],
    queryFn: async () => {
      if (!selectedUser) return null;
      const response = await authenticatedFetch(
        `${API_URL}/api/portal-admin/users/${selectedUser.id}/market-access`
      );
      if (!response || !response.ok) throw new Error('Failed to fetch user market access');
      return await response.json();
    },
    enabled: !!selectedUser,
  });

  // Fetch market users when a market is selected
  const { data: marketUsersData, isLoading: marketUsersLoading, refetch: refetchMarketUsers } = useQuery({
    queryKey: ['market-users', selectedMarket?.marketId],
    queryFn: async () => {
      if (!selectedMarket) return null;
      const response = await authenticatedFetch(
        `${API_URL}/api/portal-admin/markets/${selectedMarket.marketId}/users`
      );
      if (!response || !response.ok) throw new Error('Failed to fetch market users');
      return await response.json();
    },
    enabled: !!selectedMarket,
  });

  // Fetch country users when a country is selected
  const { data: countryUsersData, isLoading: countryUsersLoading, refetch: refetchCountryUsers } = useQuery({
    queryKey: ['country-users', selectedCountry?.countryId],
    queryFn: async () => {
      if (!selectedCountry) return null;
      const response = await authenticatedFetch(
        `${API_URL}/api/portal-admin/countries/${selectedCountry.countryId}/users`
      );
      if (!response || !response.ok) throw new Error('Failed to fetch country users');
      return await response.json();
    },
    enabled: !!selectedCountry,
  });

  // Grant access mutation
  const grantAccessMutation = useMutation({
    mutationFn: async (data: typeof grantForm) => {
      const isCountryAccess = data.market_id === 'all';
      const entityType = isCountryAccess ? 'country' : 'market';
      const entityId = isCountryAccess ? data.country_id : data.market_id;
      
      const response = await authenticatedFetch(
        `${API_URL}/api/portal-admin/users/${data.user_id}/market-access/grant`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entity_type: entityType,
            entity_id: entityId,
            access_level: data.access_level,
            expires_at: data.expires_at || undefined,
            notes: data.notes || undefined,
          }),
        }
      );
      if (!response || !response.ok) throw new Error('Failed to grant access');
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-with-market-access'] });
      queryClient.invalidateQueries({ queryKey: ['markets-with-users'] });
      queryClient.invalidateQueries({ queryKey: ['countries-with-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-market-access'] });
      setGrantAccessDialog(false);
      setGrantForm({
        user_id: '',
        country_id: null,
        market_id: null,
        access_level: 'manage',
        expires_at: '',
        notes: '',
      });
    },
  });

  // Revoke access mutation
  const revokeAccessMutation = useMutation({
    mutationFn: async ({
      userId,
      accessId,
      entityType,
      reason,
    }: {
      userId: string;
      accessId: number;
      entityType: 'market' | 'country';
      reason?: string;
    }) => {
      const response = await authenticatedFetch(
        `${API_URL}/api/portal-admin/users/${userId}/market-access/${accessId}/revoke?type=${entityType}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      );
      if (!response || !response.ok) throw new Error('Failed to revoke access');
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-with-market-access'] });
      queryClient.invalidateQueries({ queryKey: ['markets-with-users'] });
      queryClient.invalidateQueries({ queryKey: ['countries-with-users'] });
      refetchUserAccess();
    },
  });

  const allUsers = allUsersData?.data || [];
  const usersWithAccess = usersWithAccessData?.data || [];
  const marketsWithUsers = marketsWithUsersData?.data || [];
  const countriesWithUsers = countriesWithUsersData?.data || [];
  const countries = countriesData?.data || [];
  const countryMarkets = countryMarketsData?.data || [];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewUserAccess = (user: User) => {
    setSelectedUser(user);
    setViewUserAccessDialog(true);
  };

  const handleViewMarketUsers = (market: any) => {
    setSelectedMarket(market);
    setViewMarketUsersDialog(true);
  };

  const handleViewCountryUsers = (country: any) => {
    setSelectedCountry(country);
    setViewCountryUsersDialog(true);
  };

  const handleGrantAccess = () => {
    if (grantForm.user_id && grantForm.country_id && grantForm.market_id) {
      grantAccessMutation.mutate(grantForm);
    }
  };

  const handleRevokeAccess = (access: MarketAccess | CountryAccess, entityType: 'market' | 'country') => {
    if (selectedUser && window.confirm('Are you sure you want to revoke this access?')) {
      revokeAccessMutation.mutate({
        userId: selectedUser.id,
        accessId: access.id,
        entityType,
        reason: 'Revoked by admin',
      });
    }
  };

  const toggleMarketExpansion = (marketId: number) => {
    const newExpanded = new Set(expandedMarkets);
    if (newExpanded.has(marketId)) {
      newExpanded.delete(marketId);
    } else {
      newExpanded.add(marketId);
    }
    setExpandedMarkets(newExpanded);
  };

  const toggleUserExpansion = (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setGrantAccessDialog(true)}
        >
          Grant Access
        </Button>
      </Box>

      {/* Tabs */}
      <Paper elevation={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="market access tabs">
            <Tab icon={<Public />} label="By Country" iconPosition="start" />
            <Tab icon={<Map />} label="By Market" iconPosition="start" />
            <Tab icon={<Person />} label="By User" iconPosition="start" />
          </Tabs>
        </Box>

        {/* By Country Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeIndirectCountryAccess}
                  onChange={(e) => setIncludeIndirectCountryAccess(e.target.checked)}
                />
              }
              label="Include countries where users only have market-level access"
            />
          </Box>
          {countriesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : countriesWithUsers.filter((country: any) => includeIndirectCountryAccess || country.directUserCount > 0).length === 0 ? (
            <Alert severity="info">
              {includeIndirectCountryAccess 
                ? "No countries have user access configured yet."
                : "No countries have direct user access. Check the box above to see countries with market-level access."}
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Country</TableCell>
                    <TableCell>Continent</TableCell>
                    <TableCell align="center">Markets</TableCell>
                    <TableCell align="center">Direct Access</TableCell>
                    <TableCell align="center">Market Access</TableCell>
                    <TableCell align="center">Total Users</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {countriesWithUsers
                    .filter((country: any) => includeIndirectCountryAccess || country.directUserCount > 0)
                    .map((country: any) => (
                    <TableRow key={country.countryId}>
                      <TableCell>{country.countryName}</TableCell>
                      <TableCell>{country.continentName}</TableCell>
                      <TableCell align="center">
                        <Chip label={country.marketCount} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={country.directUserCount} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={country.marketUserCount} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={country.totalUserCount} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Users">
                          <IconButton onClick={() => handleViewCountryUsers(country)} size="small">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* By Market Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeIndirectMarketAccess}
                  onChange={(e) => setIncludeIndirectMarketAccess(e.target.checked)}
                />
              }
              label="Include markets where users only have country-level access"
            />
          </Box>
          {marketsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : marketsWithUsers.filter((market: any) => includeIndirectMarketAccess || market.directUserCount > 0).length === 0 ? (
            <Alert severity="info">
              {includeIndirectMarketAccess 
                ? "No markets have user access configured yet."
                : "No markets have direct user access. Check the box above to see markets with country-level access."}
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Market</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Continent</TableCell>
                    <TableCell align="center">Direct Access</TableCell>
                    <TableCell align="center">Country Access</TableCell>
                    <TableCell align="center">Total Users</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {marketsWithUsers
                    .filter((market: any) => includeIndirectMarketAccess || market.directUserCount > 0)
                    .map((market: any) => (
                    <TableRow key={market.marketId}>
                      <TableCell>{market.marketName}</TableCell>
                      <TableCell>{market.countryName}</TableCell>
                      <TableCell>{market.continentName}</TableCell>
                      <TableCell align="center">
                        <Chip label={market.directUserCount} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={market.countryUserCount} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={market.totalUserCount} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Users">
                          <IconButton onClick={() => handleViewMarketUsers(market)} size="small">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* By User Tab */}
        <TabPanel value={tabValue} index={2}>
          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : usersWithAccess.length === 0 ? (
            <Alert severity="info">No users have been granted market access yet.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Direct Markets</TableCell>
                    <TableCell align="center">Countries</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersWithAccess.map((user: any) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell align="center">
                        {user.totalMarkets > 0 && (
                          <Chip label={user.totalMarkets} size="small" />
                        )}
                        {user.totalMarkets === 0 && '-'}
                      </TableCell>
                      <TableCell align="center">
                        {user.totalCountries > 0 && (
                          <Chip label={user.totalCountries} size="small" variant="outlined" />
                        )}
                        {user.totalCountries === 0 && '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={user.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {user.lastLoginAt
                          ? format(new Date(user.lastLoginAt), 'MMM dd, yyyy')
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Market Access">
                          <IconButton onClick={() => handleViewUserAccess(user)} size="small">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Grant Access Dialog */}
      <Dialog open={grantAccessDialog} onClose={() => setGrantAccessDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Market/Country Access</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={allUsers}
              getOptionLabel={(option: User) => `${option.name || option.email} (${option.email})`}
              renderInput={(params) => <TextField {...params} label="Select User" required />}
              onChange={(_, value) => setGrantForm({ ...grantForm, user_id: value?.id || '' })}
            />

            <Autocomplete
              options={countries}
              getOptionLabel={(option: Country) => option.name}
              renderInput={(params) => <TextField {...params} label="Select Country" required />}
              onChange={(_, value) => {
                setGrantForm({ 
                  ...grantForm, 
                  country_id: value?.id || null,
                  market_id: null
                });
              }}
            />

            {grantForm.country_id && (
              <FormControl fullWidth>
                <InputLabel>Select Market</InputLabel>
                <Select
                  value={grantForm.market_id || ''}
                  onChange={(e) => setGrantForm({ 
                    ...grantForm, 
                    market_id: e.target.value as number | 'all'
                  })}
                  label="Select Market"
                >
                  <MenuItem value="all">
                    <strong>All Markets in Country</strong>
                  </MenuItem>
                  {countryMarkets.map((market: any) => (
                    <MenuItem key={market.id} value={market.id}>
                      {market.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Access Level</InputLabel>
              <Select
                value={grantForm.access_level}
                onChange={(e) => setGrantForm({ 
                  ...grantForm, 
                  access_level: e.target.value as 'view' | 'manage' | 'admin' 
                })}
                label="Access Level"
              >
                <MenuItem value="view">View</MenuItem>
                <MenuItem value="manage">Manage</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Expires At (Optional)"
              type="datetime-local"
              value={grantForm.expires_at}
              onChange={(e) => setGrantForm({ ...grantForm, expires_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Notes (Optional)"
              multiline
              rows={2}
              value={grantForm.notes}
              onChange={(e) => setGrantForm({ ...grantForm, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setGrantAccessDialog(false);
            setGrantForm({
              user_id: '',
              country_id: null,
              market_id: null,
              access_level: 'manage',
              expires_at: '',
              notes: '',
            });
          }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGrantAccess}
            disabled={!grantForm.user_id || !grantForm.country_id || !grantForm.market_id}
          >
            Grant Access
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Access Dialog */}
      <Dialog open={viewUserAccessDialog} onClose={() => setViewUserAccessDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Market Access - {selectedUser?.name || selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          {accessLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {userAccessData?.data && (
                <>
                  {/* Market Access Section */}
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Map sx={{ mr: 1 }} /> Market Access
                  </Typography>
                  {userAccessData.data.markets?.length > 0 ? (
                    <TableContainer sx={{ mb: 4 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Market</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Access Level</TableCell>
                            <TableCell>Granted By</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userAccessData.data.markets.map((access: MarketAccess) => (
                            <TableRow key={access.id}>
                              <TableCell>{access.market_name}</TableCell>
                              <TableCell>{access.country_name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={access.access_level}
                                  size="small"
                                  color={
                                    access.access_level === 'admin'
                                      ? 'error'
                                      : access.access_level === 'manage'
                                      ? 'warning'
                                      : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>{access.granted_by_name || '-'}</TableCell>
                              <TableCell>
                                {access.expires_at
                                  ? format(new Date(access.expires_at), 'MMM dd, yyyy')
                                  : 'Never'}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  onClick={() => handleRevokeAccess(access, 'market')}
                                  size="small"
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" sx={{ mb: 3 }}>No market access granted</Alert>
                  )}

                  {/* Country Access Section */}
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Public sx={{ mr: 1 }} /> Country Access
                  </Typography>
                  {userAccessData.data.countries?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Country</TableCell>
                            <TableCell>Markets</TableCell>
                            <TableCell>Access Level</TableCell>
                            <TableCell>Granted By</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userAccessData.data.countries.map((access: CountryAccess) => (
                            <TableRow key={access.id}>
                              <TableCell>{access.country_name}</TableCell>
                              <TableCell>{access.market_count || 0} markets</TableCell>
                              <TableCell>
                                <Chip
                                  label={access.access_level}
                                  size="small"
                                  color={
                                    access.access_level === 'admin'
                                      ? 'error'
                                      : access.access_level === 'manage'
                                      ? 'warning'
                                      : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>{access.granted_by_name || '-'}</TableCell>
                              <TableCell>
                                {access.expires_at
                                  ? format(new Date(access.expires_at), 'MMM dd, yyyy')
                                  : 'Never'}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  onClick={() => handleRevokeAccess(access, 'country')}
                                  size="small"
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No country access granted</Alert>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewUserAccessDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* View Market Users Dialog */}
      <Dialog open={viewMarketUsersDialog} onClose={() => setViewMarketUsersDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Users with Access - {selectedMarket?.marketName}
        </DialogTitle>
        <DialogContent>
          {marketUsersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {marketUsersData?.data && marketUsersData.data.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Access Type</TableCell>
                        <TableCell>Access Level</TableCell>
                        <TableCell>Granted At</TableCell>
                        <TableCell>Expires</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {marketUsersData.data.map((user: any) => (
                        <TableRow key={`${user.user_id}-${user.access_type}`}>
                          <TableCell>{user.name || '-'}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Chip
                              label={user.access_type === 'direct' ? 'Direct' : 'Country'}
                              size="small"
                              variant={user.access_type === 'direct' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.access_level}
                              size="small"
                              color={
                                user.access_level === 'admin'
                                  ? 'error'
                                  : user.access_level === 'manage'
                                  ? 'warning'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {format(new Date(user.granted_at), 'MMM dd, yyyy')}
                          </TableCell>
                          <TableCell>
                            {user.expires_at
                              ? format(new Date(user.expires_at), 'MMM dd, yyyy')
                              : 'Never'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No users have access to this market</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewMarketUsersDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* View Country Users Dialog */}
      <Dialog open={viewCountryUsersDialog} onClose={() => setViewCountryUsersDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Users with Access - {selectedCountry?.countryName}
        </DialogTitle>
        <DialogContent>
          {countryUsersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {countryUsersData?.data && countryUsersData.data.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Access Type</TableCell>
                        <TableCell>Access Level</TableCell>
                        <TableCell>Granted At</TableCell>
                        <TableCell>Expires</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {countryUsersData.data.map((user: any) => (
                        <TableRow key={`${user.user_id}-${user.access_type}`}>
                          <TableCell>{user.name || '-'}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Chip
                              label={user.access_type === 'direct' ? 'Country Direct' : 'Via Market'}
                              size="small"
                              variant={user.access_type === 'direct' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.access_level}
                              size="small"
                              color={
                                user.access_level === 'admin'
                                  ? 'error'
                                  : user.access_level === 'manage'
                                  ? 'warning'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {format(new Date(user.granted_at), 'MMM dd, yyyy')}
                          </TableCell>
                          <TableCell>
                            {user.expires_at
                              ? format(new Date(user.expires_at), 'MMM dd, yyyy')
                              : 'Never'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No users have access to this country</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewCountryUsersDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};